import type { OnboardingCockpitStatus } from 'src/features/advisor-book-onboarding/constants/cockpit-url-state';
import type { AdvisorBookOnboardingWizardStepSlug } from 'src/features/advisor-book-onboarding/constants/wizard-steps';
import { ADVISOR_BOOK_ONBOARDING_WIZARD_STEP_QUERY_KEY } from 'src/features/advisor-book-onboarding/constants/wizard-steps';

type BookTransferCockpitRouteParams = {
  readonly status?: OnboardingCockpitStatus;
  /** Scopes the cockpit to one advisor's book, for admin deep links out of a book upload. */
  readonly advisorId?: string;
  /** Prefills the cockpit search box (household name from the overview ribbon). */
  readonly query?: string;
  /** Opens the Bulk upload dialog once the cockpit tab mounts. */
  readonly openBulkUpload?: boolean;
};

type HouseholdOverviewRouteParams = {
  /** Opens the onboarding detail sheet once the household Overview ribbon mounts. */
  readonly openDetail?: boolean;
};

/** Query flag read by `HouseholdOnboardingStatusRibbon` to auto-open the detail sheet. */
export const HOUSEHOLD_ONBOARDING_DETAIL_QUERY_KEY = 'onboardingDetail';
export const HOUSEHOLD_ONBOARDING_DETAIL_QUERY_VALUE = '1';

function buildWelcomeStep(step: AdvisorBookOnboardingWizardStepSlug) {
  return `/advisor/dashboard/onboarding?${ADVISOR_BOOK_ONBOARDING_WIZARD_STEP_QUERY_KEY}=${step}` as const;
}

/** Blank WM Repaper workbook served from `apps/web/public`. */
export const ADMIN_ADVISOR_BOOK_UPLOAD_TEMPLATE_PATH = '/wm-repaper-template.xlsx';

export function adminBulkImportDetailPath(bulkImportId: string) {
  return `/internal/admin/ops/bulk-imports/${bulkImportId}` as const;
}

export const ADVISOR_BOOK_ONBOARDING_ROUTES = {
  welcome: '/advisor/dashboard/onboarding',
  clients: '/advisor/dashboard/clients',
  opportunities: '/advisor/dashboard/opportunities',
  flagOffRedirect: '/advisor/dashboard/clients',
  processRoot: (onboardingProcessId: string) => `/advisor/dashboard/onboarding/${onboardingProcessId}` as const,
  processStep: (onboardingProcessId: string, step: AdvisorBookOnboardingWizardStepSlug) =>
    `${ADVISOR_BOOK_ONBOARDING_ROUTES.processRoot(onboardingProcessId)}?${ADVISOR_BOOK_ONBOARDING_WIZARD_STEP_QUERY_KEY}=${step}` as const,
  // Continuous confirm → email → review flow, keyed by the household public id (uuid).
  eciAccountOnboardingFlow: (householdPublicId: string, step?: string, pageId?: string) => {
    const base = `/advisor/dashboard/onboarding/flow/${householdPublicId}` as const;
    if (step === undefined) return base;
    return pageId !== undefined ? (`${base}/${step}/${pageId}` as const) : (`${base}/${step}` as const);
  },
  welcomeStep: buildWelcomeStep,
  householdOverview: (householdUuid: string, { openDetail }: HouseholdOverviewRouteParams = {}) => {
    if (openDetail === true) {
      return `/advisor/dashboard/client/${householdUuid}?${HOUSEHOLD_ONBOARDING_DETAIL_QUERY_KEY}=${HOUSEHOLD_ONBOARDING_DETAIL_QUERY_VALUE}` as const;
    }
    return `/advisor/dashboard/client/${householdUuid}` as const;
  },
  bookTransferCockpit: ({ status, advisorId, query, openBulkUpload }: BookTransferCockpitRouteParams = {}) => {
    const params = new URLSearchParams({ tab: 'onboarding' });
    if (status !== undefined) {
      params.set('cockpitStatus', status);
    }
    if (advisorId !== undefined) {
      params.set('cockpitAdvisor', advisorId);
    }
    if (query !== undefined && query !== '') {
      params.set('cockpitQuery', query);
    }
    if (openBulkUpload === true) {
      params.set('cockpitOpenBulkUpload', 'true');
    }
    return `/advisor/dashboard/opportunities?${params.toString()}` as const;
  },
} as const;
